import { Center, Popover, Portal } from '@chakra-ui/react';
import { StickerSetType } from '@halo/chat-sdk';
import { Emoji, type EmojiProps, MessageEmojiIcon, Tooltip } from '@halo/ui-chat-react';
import { useRepairFocus } from '@halo/ui-react';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface EmojiButtonProps {
  onSelect: EmojiProps['onSelect'];
  mini?: boolean;
}

function EmojiButton({ onSelect, mini = false }: EmojiButtonProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { handleOpenChange } = useRepairFocus();
  const triggerId = useId();

  const handleSelect = useCallback<EmojiProps['onSelect']>(
    (emojiId, groupId, type) => {
      onSelect(emojiId, groupId, type);
      setOpen(false);
    },
    [onSelect]
  );

  return (
    <Popover.Root
      ids={{ trigger: triggerId }}
      open={open}
      lazyMount
      unmountOnExit
      onOpenChange={(e) => {
        setOpen(e.open);
        handleOpenChange(e);
      }}
    >
      <Tooltip
        ids={{ trigger: triggerId }}
        content={t('chat.tooltipEmoji')}
        chakraPortal={false}
        openDelay={0}
        closeDelay={0}
        showArrow={true}
      >
        <Popover.Trigger asChild>
          <Center
            h="1.857em"
            w="1.857em"
            cursor="pointer"
            borderRadius="sm"
            _hover={{ bg: 'bg.muted', color: 'brand.100' }}
            transition="all 0.2s"
          >
            <Center className="shake">
              <MessageEmojiIcon />
            </Center>
          </Center>
        </Popover.Trigger>
      </Tooltip>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            animationName={{
              _open: 'slide-from-bottom, fade-in',
              _closed: 'slide-to-bottom, fade-out',
            }}
            animationDuration="slower"
            bg="transparent"
            border="none"
            shadow="none"
            w="auto"
          >
            <Emoji onSelect={handleSelect} mini={mini} defaultTab={StickerSetType.Emoji} />
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}

const MemoizedEmojiButton = memo(EmojiButton);

export { MemoizedEmojiButton as EmojiButton };
